/**
 * EVENTSFLO-LOGIN-PORTAL-CHOOSER-LINKS-V1
 *
 * Small portal chooser used at the foot of the Admin and Client login pages.
 * Helps users who land on the wrong portal find the right one without
 * having to remember technical routes.
 *
 * Props
 *   currentPortal: "admin" | "client" | "crew"
 *     The portal the user is currently on. The chooser renders the OTHER
 *     two portals as quick-jump links.
 *   email?: string
 *     Optional email to forward to the target portal's login page via
 *     `?email=` so the user does not retype it. Never carries the password.
 *
 * Behaviour notes
 *   - Uses react-router-dom `Link` (SPA navigation, preserves React state).
 *   - The Crew Portal link points to `/crew`. Unauthenticated crew users
 *     will be redirected back to `/login` (the shared admin+crew login
 *     form) by CrewPortalPage's own guard.
 *   - The "Request a quote" link is always shown so prospective clients
 *     who haven't been onboarded still have a clear path.
 */

import React from "react";
import { Link } from "react-router-dom";
import { Building2, Users, Briefcase, FileText } from "lucide-react";

const PORTALS = {
  admin: {
    label: "Admin Portal",
    href: "/login",
    icon: Briefcase,
    description: "For iCrew Services internal operations",
    testid: "portal-link-admin",
  },
  client: {
    label: "Client Portal",
    href: "/client/login",
    icon: Building2,
    description:
      "Clients: view requests, projects, quotes and invoices",
    testid: "portal-link-client",
  },
  crew: {
    label: "Crew Portal",
    href: "/crew",
    icon: Users,
    description:
      "Crew: onboarding, documents, assignments and availability",
    testid: "portal-link-crew",
  },
};

function withEmail(href, email) {
  if (!email) return href;
  const sep = href.includes("?") ? "&" : "?";
  return `${href}${sep}email=${encodeURIComponent(email.trim().toLowerCase())}`;
}

export default function PortalChooserLinks({ currentPortal, email }) {
  const others = Object.entries(PORTALS).filter(
    ([key]) => key !== currentPortal
  );

  return (
    <div
      className="w-full border-t border-border/40 pt-4 mt-2"
      data-testid="portal-chooser"
    >
      <p
        className="text-xs text-muted-foreground text-center mb-3"
        data-testid="portal-chooser-heading"
      >
        Looking for another portal?
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {others.map(([key, p]) => {
          const Icon = p.icon;
          // Forward email only to Admin/Client login pages (where it can
          // prefill). The Crew Portal entry doesn't accept ?email=.
          const href =
            key === "crew" ? p.href : withEmail(p.href, email);
          return (
            <Link
              key={key}
              to={href}
              className="flex items-start gap-2 rounded-md border border-border/40 hover:border-primary/50 hover:bg-primary/5 transition-colors px-3 py-2 text-left"
              data-testid={p.testid}
            >
              <Icon className="h-4 w-4 mt-0.5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-foreground">
                  {p.label}
                </div>
                <div className="text-[11px] leading-snug text-muted-foreground">
                  {p.description}
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <Link
        to="/request-quote"
        className="mt-2 flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
        data-testid="portal-link-request-quote"
      >
        <FileText className="h-3.5 w-3.5" />
        Just looking for a quote? Request crew here
      </Link>
    </div>
  );
}
